import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, AlertTriangle, UserRound, MessageSquareText, Ban } from "lucide-react";
import { api, errCode } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useMaster, nameOf } from "../lib/master";
import { fmtDateTime, fmtTime, kg } from "../lib/time";
import { PageTitle, Inp } from "../components/Common";
import { StatusBadge } from "../components/Badges";
import { ScopeFilters } from "./Bins";
import { GuideButton } from "./Incoming";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const clean = (q) => Object.fromEntries(Object.entries(q).filter(([, v]) => v));

export const PendingActions = ({ b, reload }) => {
  const { t, te } = useLang();
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const save = () => api.post(`/bins/${b.id}/${mode === "close" ? "close" : "undispatched-reason"}`, { reason })
    .then(() => { toast.success(t("saved")); setMode(null); setReason(""); reload(); }).catch((e) => toast.error(te(errCode(e))));
  return (
    <>
      <div className="flex gap-1 flex-wrap">
        <button className="btn-secondary btn-sm" onClick={() => setMode("reason")} data-testid={`pending-reason-${b.label_number}`}><MessageSquareText size={14} />{t("dp_reason")}</button>
        <button className="btn-secondary btn-sm" onClick={() => setMode("close")} data-testid={`pending-close-${b.label_number}`}><Ban size={14} />{t("dp_close")}</button>
      </div>
      <Dialog open={!!mode} onOpenChange={(o) => !o && setMode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{b.label_number} · {mode === "close" ? t("dp_close") : t("dp_reason")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{mode === "close" ? t("dp_close_hint") : t("dp_reason_hint")}</p>
          <Inp label={t("reason")} value={reason} onChange={(e) => setReason(e.target.value)} testid="pending-reason-input" />
          <button className={`btn-primary w-full ${mode === "close" ? "!bg-red-700 hover:!bg-red-800" : ""}`} disabled={reason.trim().length < 3} onClick={save} data-testid="pending-save-button">{t("save")}</button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const PendingList = ({ rows, reload, compact }) => {
  const { t } = useLang();
  const farms = useMaster("farms");
  const sectors = useMaster("sectors");
  if (!rows.length) return null;
  return (
    <div className="card border-2 border-red-300 overflow-hidden" data-testid="pending-dispatch">
      <div className="px-4 py-3 bg-red-50 border-b border-red-200">
        <p className="font-bold text-red-800 flex items-center gap-2"><AlertTriangle size={18} />{t("dp_pending_title")} ({rows.length})</p>
        <p className="text-xs text-red-700 mt-1">{t("dp_pending_hint")}</p>
      </div>
      <div className="overflow-x-auto max-h-[420px]">
        <table className="tbl w-full"><thead><tr><th>{t("label")}</th><th>{t("farm")} / {t("sector")}</th><th>{t("status")}</th><th>{t("dp_activated")}</th>{!compact && <th></th>}</tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} data-testid={`pending-row-${b.label_number}`}>
              <td><Link to={`/bin/${b.id}`} className="font-mono font-bold text-[#800B28] underline">{b.label_number}</Link></td>
              <td className="text-sm">{nameOf(farms, b.farm_id)}<br /><span className="text-xs text-slate-500">{nameOf(sectors, b.sector_id)}</span></td>
              <td><StatusBadge status={b.status} /></td>
              <td className="font-mono text-xs">{b.activated_date}</td>
              {!compact && <td><PendingActions b={b} reload={reload} /></td>}
            </tr>))}</tbody></table>
      </div>
      {compact && <Link to="/dispatch" className="block px-4 py-3 font-bold text-[#800B28] border-t" data-testid="pending-open-dispatch">{t("dp_open")} →</Link>}
    </div>
  );
};

const DriverForm = ({ f, setF }) => {
  const { t } = useLang();
  const [drivers, setDrivers] = useState([]);
  useEffect(() => { api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3">
      {!!drivers.length && <div><p className="eyebrow mb-1">{t("dp_recent_drivers")}</p>
        <div className="flex flex-wrap gap-2" data-testid="recent-drivers">{drivers.map((d) => (
          <button key={d.id} onClick={() => setF({ driver_name: d.name, truck_plate: d.truck_plate })} data-testid={`driver-chip-${d.truck_plate}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full border-2 text-sm font-semibold transition-colors ${f.driver_name === d.name && f.truck_plate === d.truck_plate ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300 hover:border-slate-500"}`}>
            <UserRound size={14} />{d.name} · <span className="font-mono">{d.truck_plate}</span></button>))}</div></div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <Inp label={t("dp_driver")} value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} testid="driver-name-input" />
        <Inp label={t("dp_plate")} value={f.truck_plate} onChange={(e) => setF({ ...f, truck_plate: e.target.value.toUpperCase() })} testid="truck-plate-input" />
      </div>
    </div>
  );
};

export default function Dispatch() {
  const { t, te, lang } = useLang();
  const farms = useMaster("farms");
  const sectors = useMaster("sectors");
  const [q, setQ] = useState({ grower_id: "", farm_id: "" });
  const [ready, setReady] = useState([]);
  const [pending, setPending] = useState([]);
  const [done, setDone] = useState([]);
  const [sel, setSel] = useState([]);
  const [f, setF] = useState({ driver_name: "", truck_plate: "" });
  const [busy, setBusy] = useState(false);
  const load = () => {
    const err = (e) => toast.error(te(errCode(e)));
    api.get("/bins", { params: clean({ ...q, status: "verified" }) }).then((r) => setReady(r.data)).catch(err);
    api.get("/dispatch/pending", { params: clean({ farm_id: q.farm_id }) }).then((r) => setPending(r.data)).catch(err);
    api.get("/dispatches", { params: clean({ farm_id: q.farm_id }) }).then((r) => setDone(r.data)).catch(err);
  };
  useEffect(() => { load(); setSel([]); }, [q.grower_id, q.farm_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const chosen = ready.filter((b) => sel.includes(b.id));
  const farmOf = chosen[0]?.farm_id;
  const toggle = (b) => {
    if (sel.includes(b.id)) return setSel(sel.filter((x) => x !== b.id));
    if (farmOf && b.farm_id !== farmOf) return toast.error(t("err_one_farm_per_dispatch"));
    setSel([...sel, b.id]);
  };
  const allFarm = farmOf || ready[0]?.farm_id;
  const selectAll = () => setSel(sel.length ? [] : ready.filter((b) => b.farm_id === allFarm).map((b) => b.id));
  const dispatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/dispatches", { bin_ids: sel, ...f, client_at: new Date().toISOString() });
      toast.success(`${data.number} · ${data.bin_count} bins`); setSel([]); load();
    } catch (e) { toast.error(te(errCode(e))); }
    setBusy(false);
  };

  return (
    <div className={`space-y-5 ${sel.length ? "pb-[380px] sm:pb-[300px]" : ""}`} data-testid="dispatch-page">
      <PageTitle>{t("dp_title")}</PageTitle>
      <ScopeFilters q={q} setQ={setQ} noDate />
      <PendingList rows={pending} reload={load} />
      <section className="card overflow-hidden" data-testid="ready-to-dispatch">
        <div className="px-4 py-3 border-b flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-bold text-lg">{t("dp_ready")} ({ready.length})</h2>
          {!!ready.length && <button className="btn-secondary btn-sm" onClick={selectAll} data-testid="select-all-button">{sel.length ? t("dp_clear") : t("dp_select_all")}</button>}
        </div>
        <p className="px-4 pt-2 text-xs text-slate-500">{t("dp_ready_hint")}</p>
        <div className="overflow-x-auto max-h-[460px]">
          <table className="tbl w-full"><thead><tr><th></th><th>{t("label")}</th><th>{t("farm")} / {t("sector")}</th><th>{t("dp_net")}</th><th>{t("dp_weighed")}</th></tr></thead>
            <tbody>{ready.map((b) => (
              <tr key={b.id} className={`cursor-pointer ${sel.includes(b.id) ? "bg-emerald-50" : ""}`} onClick={() => toggle(b)} data-testid={`ready-row-${b.label_number}`}>
                <td><input type="checkbox" className="w-5 h-5" checked={sel.includes(b.id)} readOnly data-testid={`ready-check-${b.label_number}`} /></td>
                <td className="font-mono font-bold">{b.label_number}</td>
                <td className="text-sm">{nameOf(farms, b.farm_id)}<br /><span className="text-xs text-slate-500">{nameOf(sectors, b.sector_id)}</span></td>
                <td className="font-mono">{kg(b.dock?.net_kg)}</td>
                <td className="font-mono text-xs">{fmtDateTime(b.dock?.at, lang)}</td>
              </tr>))}
              {!ready.length && <tr><td colSpan={5} className="text-slate-500">{t("none")}</td></tr>}</tbody></table>
        </div>
      </section>
      {!!sel.length && <section className="card p-4 space-y-3 border-2 border-slate-900 sticky bottom-3 bg-white shadow-xl" data-testid="dispatch-form">
        <p className="font-bold text-lg" data-testid="dispatch-summary">{sel.length} bins · {kg(chosen.reduce((s, b) => s + (b.dock?.net_kg || 0), 0))} · {nameOf(farms, farmOf)}</p>
        <DriverForm f={f} setF={setF} />
        <button className="btn-primary w-full" disabled={busy || f.driver_name.trim().length < 2 || f.truck_plate.trim().length < 2} onClick={dispatch} data-testid="dispatch-submit-button">
          <Truck size={20} />{busy ? "…" : t("dp_submit")}</button>
      </section>}
      <section className="card overflow-x-auto" data-testid="dispatches-today">
        <h2 className="font-bold text-lg px-4 py-3 border-b">{t("dp_today")} ({done.length})</h2>
        <table className="tbl w-full"><thead><tr><th>N°</th><th>{t("dp_time")}</th><th>{t("farm")}</th><th>{t("dp_driver")}</th><th>{t("dp_plate")}</th><th>Bins</th><th>{t("dp_net")}</th><th>{t("dp_by")}</th><th></th></tr></thead>
          <tbody>{done.map((d) => (
            <tr key={d.id} data-testid={`dispatch-row-${d.number}`}><td className="font-mono font-bold">{d.number}</td><td className="font-mono text-xs">{fmtTime(d.dispatched_at, lang)}</td>
              <td className="text-sm">{nameOf(farms, d.farm_id)}</td><td>{d.driver_name}</td><td className="font-mono">{d.truck_plate}</td>
              <td className="font-mono">{d.bin_count}</td><td className="font-mono">{kg(d.net_kg)}</td><td className="text-xs">{d.dispatched_by}</td><td><GuideButton d={d} /></td></tr>))}
            {!done.length && <tr><td colSpan={9} className="text-slate-500">{t("none")}</td></tr>}</tbody></table>
      </section>
    </div>
  );
}
